/**
 * Transfer wizard staff/resident actions and commit flows.
 */
import { useCallback, type MutableRefObject } from 'react'
import { fetchCurrentUser } from '@/features/auth/authSlice'
import { trackApplicationEvent } from '@/lib/utils/analytics'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import type { PropertyStaffRow } from '../types'
import * as transferApi from '../api/transferApi'
import { getFilterValue, validateTransferData } from '../utils/transferValidateData'
import { getSelectAllResidentSelectionState } from '../utils/transferResidentSelection'
import {
  draftTransferStaff,
  selectAllTransferStaff,
  userSelectedTransferStaff,
} from '../utils/transferProcessStaffUtils'
import { normalizeSplitPropertyId } from '../utils/normalizeSplitPropertyId'
import type { TransferProcessWorkflowDeps } from './transferWorkflowTypes'
import type { TransferProgressFlagOverrides } from '../utils/transferProcessProgressPayload'

const AFTER_STAFF_COMMIT_FLAGS: TransferProgressFlagOverrides = { commitDone: true }

type LoaderRefs = {
  urlParamsRef: MutableRefObject<{
    page: number
    perPage: number
    sortBy: string
    orderBy: string
    past_customer: string
  }>
  urlParamsAppRef: MutableRefObject<{
    page: number
    perPage: number
    sortBy: string
    orderBy: string
  }>
}

type LoaderApi = {
  loadStaffs: (type?: 'aftercommit', flagOverrides?: TransferProgressFlagOverrides) => Promise<void>
  loadAddedStaffs: () => Promise<void>
  loadResidents: () => Promise<void>
  loadApplications: () => Promise<void>
}

type ProgressApi = {
  saveTransferProgress: (flagOverrides?: TransferProgressFlagOverrides) => Promise<void>
}

export function useTransferProcessActions(
  { dispatch, routing, dialogs, form, core }: TransferProcessWorkflowDeps,
  loaders: LoaderApi & LoaderRefs,
  progress: ProgressApi,
) {
  const { fragment, entityId } = routing
  const { openFlowConfirm, bumpTransferLogNotesRefresh, setStaffSelectionOpen } = dialogs
  const { getValues } = form
  const {
    removestaffList,
    transferstaffList,
    addedstaffList,
    selectedRemoveRoleList,
    selectedTransferRoleList,
    selectedAddRoleList,
    residents,
    applications,
    pageOptions,
    pageOptionsApplication,
    selectedResidentList,
    selectedApplicationList,
    newPropertySave,
    selectedTab,
    selectedSubTab,
    primaryContactSet,
    setProcessing,
    setRemovestaffList,
    setTransferstaffList,
    setSelectedTransferRoleList,
    setSelectedRemoveRoleList,
    setSelectedAddRoleList,
    setSelectedResidentList,
    setSelectAllResidentChecked,
    setSelectedApplicationList,
    setSelectAllApplicationChecked,
    setEmpDone,
    setResidentcount,
    setApplicantcount,
    setCommitDone,
    setSelectedSubTab,
    setVerifyDone,
    setPrimaryContactSet,
    setResidentDone,
    setInfoDone,
    setSelectedTab,
    setNewPropertySave,
  } = core

  const { urlParamsRef, urlParamsAppRef, loadStaffs, loadAddedStaffs, loadResidents, loadApplications } =
    loaders
  const { saveTransferProgress } = progress

  const refreshAuthUser = useCallback(() => {
    void dispatch(fetchCurrentUser())
  }, [dispatch])

  const finishStaffTransferStep = useCallback(async () => {
    setCommitDone(true)
    setSelectedSubTab('addstaff')
    await loadStaffs('aftercommit', AFTER_STAFF_COMMIT_FLAGS)
    await loadAddedStaffs()
    urlParamsRef.current.page = 1
    urlParamsAppRef.current.page = 1
    await loadResidents()
    await loadApplications()
    bumpTransferLogNotesRefresh()
  }, [
    setCommitDone,
    setSelectedSubTab,
    loadStaffs,
    loadAddedStaffs,
    urlParamsRef,
    urlParamsAppRef,
    loadResidents,
    loadApplications,
    bumpTransferLogNotesRefresh,
  ])

  const draftTransferStaffHandler = useCallback(
    (type: 'remove' | 'transfer') => {
      draftTransferStaff(
        type,
        { transferstaffList, selectedTransferRoleList, removestaffList, selectedRemoveRoleList },
        {
          setRemovestaffList,
          setTransferstaffList,
          setSelectedTransferRoleList,
          setSelectedRemoveRoleList,
        },
      )
    },
    [
      transferstaffList,
      selectedTransferRoleList,
      removestaffList,
      selectedRemoveRoleList,
      setRemovestaffList,
      setTransferstaffList,
      setSelectedTransferRoleList,
      setSelectedRemoveRoleList,
    ],
  )

  const selectAllTransfer = useCallback(
    (checked: boolean, kind: 'remove' | 'transfer' | 'new') => {
      selectAllTransferStaff(
        checked,
        kind,
        { removestaffList, transferstaffList, addedstaffList },
        { setSelectedRemoveRoleList, setSelectedTransferRoleList, setSelectedAddRoleList },
      )
    },
    [
      removestaffList,
      transferstaffList,
      addedstaffList,
      setSelectedRemoveRoleList,
      setSelectedTransferRoleList,
      setSelectedAddRoleList,
    ],
  )

  const userSelectedTransfer = useCallback(
    (checked: boolean, user: PropertyStaffRow, type: 'remove' | 'transfer' | 'new') => {
      userSelectedTransferStaff(
        checked,
        user,
        type,
        { selectedRemoveRoleList, selectedTransferRoleList, selectedAddRoleList },
        { setSelectedRemoveRoleList, setSelectedTransferRoleList, setSelectedAddRoleList },
      )
    },
    [
      selectedRemoveRoleList,
      selectedTransferRoleList,
      selectedAddRoleList,
      setSelectedRemoveRoleList,
      setSelectedTransferRoleList,
      setSelectedAddRoleList,
    ],
  )

  const selectAllResident = useCallback(
    (checked: boolean) => {
      const { selectedIds, selectAllChecked } = getSelectAllResidentSelectionState(
        checked,
        residents,
      )
      setSelectedResidentList(selectedIds)
      setSelectAllResidentChecked(selectAllChecked)
    },
    [residents, setSelectedResidentList, setSelectAllResidentChecked],
  )

  const selectAllApplication = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedApplicationList(applications.map((r) => r.application_id))
        setSelectAllApplicationChecked(true)
      } else {
        setSelectedApplicationList([])
        setSelectAllApplicationChecked(false)
      }
    },
    [applications, setSelectedApplicationList, setSelectAllApplicationChecked],
  )

  const transferResident = useCallback(async () => {
    let transferall = false
    let messageData = 'Are you sure want to transfer '
    if (
      (selectedResidentList.length &&
        pageOptions.total > 10 &&
        selectedResidentList.length === residents.length) ||
      (selectedApplicationList.length &&
        pageOptionsApplication.total > 10 &&
        selectedApplicationList.length === applications.length)
    ) {
      messageData += ' all residents and all applications '
      transferall = true
    } else {
      if (selectedResidentList.length) messageData += ` ${selectedResidentList.length} residents, `
      if (selectedApplicationList.length) {
        messageData += ` ${selectedApplicationList.length} pending applications`
      }
    }
    openFlowConfirm({
      title: 'Confirm resident transfer?',
      message: `${messageData.trim()}?`,
      onProceed: async () => {
        const requestData = validateTransferData(
          getFilterValue(getValues() as unknown as Record<string, unknown>),
        ) as Record<string, unknown>
        requestData.old_property_id = entityId
        requestData.type = fragment
        if (transferall) requestData.transfer_all = true
        else requestData.application_id = [...selectedResidentList, ...selectedApplicationList]
        setProcessing(true)
        try {
          // Angular `transferResident()`: notify, reload lists, set empDone + counts — does NOT
          // set residentDone, save progress, or navigate. Finish (`next()`) advances to Summary.
          await transferApi.postTransferResidents(requestData)
          trackApplicationEvent('resident_transfer_started', 'transfer')
          showSuccess('Residents Transfer Successfully!')
          await loadResidents()
          await loadApplications()
          setEmpDone(true)
          setResidentcount(transferall ? pageOptions.total : selectedResidentList.length)
          setApplicantcount(
            transferall ? pageOptionsApplication.total : selectedApplicationList.length,
          )
          bumpTransferLogNotesRefresh()
        } catch (e) {
          showApiError(e)
        } finally {
          setProcessing(false)
        }
      },
    })
  }, [
    selectedResidentList,
    pageOptions.total,
    residents.length,
    selectedApplicationList,
    pageOptionsApplication.total,
    applications.length,
    openFlowConfirm,
    getValues,
    entityId,
    fragment,
    setProcessing,
    loadResidents,
    loadApplications,
    setEmpDone,
    setResidentcount,
    setApplicantcount,
    bumpTransferLogNotesRefresh,
  ])

  const completeStaffTransfer = useCallback(async () => {
    // Split commits go through completeSplitPropertyTransfer (POST /api/split_property).
    if (fragment === 'split_property') return
    const msg =
      removestaffList.length > 0
        ? `${removestaffList.length} employees will be removed, Do you want to proceed?`
        : 'Do you want to proceed?'
    openFlowConfirm({
      title: 'Complete Transfer?',
      message: `${msg}\n\nPlease confirm that you want to proceed.`,
      onProceed: async () => {
        let requestData: Record<string, unknown> = {}
        const fv = getValues()
        const base = validateTransferData(
          getFilterValue(fv as unknown as Record<string, unknown>),
        ) as Record<string, unknown>
        if (fragment === 'transfer_property') {
          requestData = { ...base }
          requestData.company_id = fv.fk_company_id
          requestData.property_id = entityId
          setProcessing(true)
          try {
            if (removestaffList.length) {
              requestData.delete_staff = removestaffList.map((e) => e.role_id)
            }
            if (transferstaffList.length) {
              requestData.move_staff = {
                role_ids: transferstaffList.map((e) => e.role_id),
                type: 'PROPERTY',
              }
              if (fv.updatedomain === '1' && fv.newdomain) {
                ;(requestData.move_staff as Record<string, unknown>).newdomain = fv.newdomain
              }
            }
            requestData.transfer_applications = true
            await transferApi.postTransferPropertyToCompany(requestData)
            trackApplicationEvent('transfer_committed', 'transfer', { transfer_kind: 'transfer_property' })
            showSuccess('Transfer Process Commited Successfully')
            refreshAuthUser()
            await finishStaffTransferStep()
          } catch (e) {
            showApiError(e)
          } finally {
            setProcessing(false)
          }
          return
        }
        if (fragment === 'transfer_company') {
          requestData = { ...base }
          requestData.old_company_id = entityId
          requestData.type = fragment
          setProcessing(true)
          try {
            if (removestaffList.length) {
              requestData.delete_staff = removestaffList.map((e) => e.role_id)
            }
            if (transferstaffList.length) {
              requestData.move_staff = {
                role_ids: transferstaffList.map((e) => e.role_id),
                type: 'COMPANY',
              }
              if (fv.updatedomain === '1' && fv.newdomain) {
                ;(requestData.move_staff as Record<string, unknown>).newdomain = fv.newdomain
              }
            }
            requestData.transfer_applications = true
            await transferApi.postTransferCompany(requestData)
            trackApplicationEvent('transfer_committed', 'transfer', { transfer_kind: 'transfer_company' })
            showSuccess('Transfer Process Commited Successfully')
            refreshAuthUser()
            await finishStaffTransferStep()
          } catch (e) {
            showApiError(e)
          } finally {
            setProcessing(false)
          }
          return
        }
        if (fragment === 'merge_property') {
          requestData = { ...base }
          requestData.property_id = entityId
          requestData.old_property_id = fv.property_id
          requestData.type = fragment
          setProcessing(true)
          try {
            if (removestaffList.length) {
              requestData.delete_staff = removestaffList.map((e) => e.role_id)
            }
            if (transferstaffList.length) {
              requestData.move_staff = {
                role_ids: transferstaffList.map((e) => e.role_id),
                type: 'PROPERTY',
              }
              if (fv.updatedomain === '1' && fv.newdomain) {
                ;(requestData.move_staff as Record<string, unknown>).newdomain = fv.newdomain
              }
            }
            requestData.transfer_applications = true
            await transferApi.postMergeProperty(requestData)
            trackApplicationEvent('transfer_committed', 'transfer', { transfer_kind: 'merge_property' })
            showSuccess('Transfer Process Commited Successfully')
            refreshAuthUser()
            await finishStaffTransferStep()
          } catch (e) {
            showApiError(e)
          } finally {
            setProcessing(false)
          }
        }
      },
    })
  }, [
    fragment,
    removestaffList,
    transferstaffList,
    openFlowConfirm,
    getValues,
    entityId,
    setProcessing,
    refreshAuthUser,
    finishStaffTransferStep,
  ])

  const completeSplitPropertyTransfer = useCallback(async () => {
    if (selectedTab === 'propertyinfo' && newPropertySave) {
      setSelectedTab('contactsinfo')
      return
    }
    openFlowConfirm({
      title: 'Transfer Progress?',
      message: 'Do you want to proceed?',
      onProceed: async () => {
        const requestData: Record<string, unknown> = {}
        const fv = getValues()
        setProcessing(true)
        try {
          if (selectedTab === 'propertyinfo') {
            const old = validateTransferData(
              getFilterValue(fv as unknown as Record<string, unknown>),
            ) as Record<string, unknown>
            old.property_id = entityId
            delete old.new_property_name
            delete old.newpropertyinfo
            delete old.company_name
            delete old.updatedomain
            if (old.old_company_id) {
              old.fk_company_id = old.old_company_id
            }
            delete old.old_company_id
            const mergedNew = {
              ...validateTransferData(getFilterValue(fv as unknown as Record<string, unknown>)),
              ...validateTransferData(
                getFilterValue(fv.newpropertyinfo as unknown as Record<string, unknown>),
              ),
            } as Record<string, unknown>
            mergedNew.property_name = mergedNew.new_property_name
            delete mergedNew.newpropertyinfo
            delete mergedNew.new_property_name
            delete mergedNew.company_name
            delete mergedNew.updatedomain
            delete mergedNew.old_company_id
            requestData.old = old
            requestData.new = mergedNew
          } else if (selectedTab === 'contactsinfo') {
            requestData.old_property_id = entityId
            requestData.property_id = newPropertySave
            if (transferstaffList.length) {
              requestData.move_staff = {
                role_ids: transferstaffList.map((e) => e.role_id),
                type: 'PROPERTY',
              }
              if (fv.updatedomain === '1' && fv.newdomain) {
                ;(requestData.move_staff as Record<string, unknown>).newdomain = fv.newdomain
              }
            }
          } else if (selectedTab === 'residentinfo') {
            requestData.old_property_id = entityId
            requestData.property_id = newPropertySave
            requestData.type = fragment
            requestData.application_id = [...selectedResidentList, ...selectedApplicationList]
          }
          const res = await transferApi.postSplitProperty(requestData)
          trackApplicationEvent('transfer_committed', 'transfer', { transfer_kind: 'split_property' })
          showSuccess('Transfer Process Commited Successfully')
          if (selectedTab === 'propertyinfo') {
            const newId = normalizeSplitPropertyId(res)
            setNewPropertySave(newId)
            setInfoDone(true)
            // Angular: saveTransferProgress() then immediately advance tab (does not await save).
            setSelectedTab('contactsinfo')
            void saveTransferProgress({ infoDone: true, newPropertySave: newId })
            refreshAuthUser()
            bumpTransferLogNotesRefresh()
          } else if (selectedTab === 'contactsinfo') {
            await finishStaffTransferStep()
            await saveTransferProgress(AFTER_STAFF_COMMIT_FLAGS)
            if (selectedSubTab === 'transferstaff') {
              setSelectedSubTab('addstaff')
            } else {
              setSelectedTab('residentinfo')
            }
            refreshAuthUser()
            bumpTransferLogNotesRefresh()
          } else if (selectedTab === 'residentinfo') {
            const residentCount = selectedResidentList.length
            const applicantCount = selectedApplicationList.length
            setResidentDone(true)
            setEmpDone(true)
            setResidentcount(residentCount)
            setApplicantcount(applicantCount)
            // Angular: mutate flags, save, then advance — set Summary before any remount risk.
            setSelectedTab('summaryinfo')
            void saveTransferProgress({
              residentDone: true,
              empDone: true,
              residentcount: residentCount,
              applicantcount: applicantCount,
            })
            refreshAuthUser()
            bumpTransferLogNotesRefresh()
          }
        } catch (e) {
          showApiError(e)
        } finally {
          setProcessing(false)
        }
      },
    })
  }, [
    selectedTab,
    newPropertySave,
    openFlowConfirm,
    getValues,
    setProcessing,
    entityId,
    transferstaffList,
    fragment,
    selectedResidentList,
    selectedApplicationList,
    refreshAuthUser,
    finishStaffTransferStep,
    setNewPropertySave,
    setInfoDone,
    setResidentDone,
    setEmpDone,
    setResidentcount,
    setApplicantcount,
    saveTransferProgress,
    selectedSubTab,
    setSelectedSubTab,
    setSelectedTab,
    bumpTransferLogNotesRefresh,
  ])

  const completeVerify = useCallback(async () => {
    if (
      fragment === 'split_property' &&
      !primaryContactSet &&
      (addedstaffList.length > 0 || transferstaffList.length > 0)
    ) {
      setStaffSelectionOpen(true)
      return
    }
    setVerifyDone(true)
    await saveTransferProgress({ verifyDone: true })
  }, [
    fragment,
    primaryContactSet,
    addedstaffList.length,
    transferstaffList.length,
    setStaffSelectionOpen,
    setVerifyDone,
    saveTransferProgress,
  ])

  const onPrimaryContactSave = useCallback(
    async (payload: { name: string; mobile_no: string }) => {
      setProcessing(true)
      try {
        await transferApi.postPropertyJson(newPropertySave, {
          contact_name: payload.name,
          contact_phone: payload.mobile_no,
        })
        showSuccess('Primary Contact Added Successfully!')
        setVerifyDone(true)
        setPrimaryContactSet(true)
        setStaffSelectionOpen(false)
        await saveTransferProgress({ verifyDone: true })
      } catch (e) {
        showApiError(e)
      } finally {
        setProcessing(false)
      }
    },
    [
      newPropertySave,
      setProcessing,
      setVerifyDone,
      setPrimaryContactSet,
      setStaffSelectionOpen,
      saveTransferProgress,
    ],
  )

  return {
    draftTransferStaff: draftTransferStaffHandler,
    selectAllTransfer,
    userSelectedTransfer,
    selectAllResident,
    selectAllApplication,
    transferResident,
    completeStaffTransfer,
    completeSplitPropertyTransfer,
    completeVerify,
    onPrimaryContactSave,
  }
}
